import React, { useCallback, useContext, useEffect, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useForm, FormProvider } from 'react-hook-form';
import { DateTime } from 'luxon';
import styled from 'styled-components/native';
import DataTable from '@preflighttech/preflight-tables';

import { AuthContext } from 'app/src/contexts/AuthContext';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import { Fields, defaultValues } from 'app/src/elements/hookForms';
import minimalTable from 'app/src/elements/minimalTable';
import NestedFields from 'app/src/elements/NestedFields';
import MutationErrors from 'app/src/elements/MutationErrors';
import DataState from 'app/src/components/DataState';
import { Text } from 'app/src/styles';
import { currency } from 'app/src/utils/stringHelpers';

import { INVOICE_SELECTS } from './queries';

import { CREATE_TXN } from '../Txns/queries';
import METHODS from '../Txns/methods';
import { specialAccount, prepareTxnInput, } from '../Txns/utils';

export const Section = styled(View)`
  margin-top: 10px;
`;

export const FIELDS = {
  bankDate: {
    label: 'Date',
    type: 'date',
    rules: { required: true },
  },
  reference: {
    label: 'Invoice #',
  },
  name: {
    type: 'entityName',
    preferredTypes: ['Buyer'],
  },
  paymentTermId: {
    label: 'Payment Terms',
    select: [],
    rules: { required: false },
    blank: 'null',
  },
  memo: {},
};

export const LINE_FIELDS = {
  accountId: {
    label: 'Account',
    type: 'account',
    preferredTypes: ['Income'],
  },
  creditLessDebit: {
    label: 'Amount',
  },
  assetId: {
    label: 'Asset',
    type: 'asset',
    width: 140,
    extraLabels: {
      saleAssetId: 'Lot/Sale',
      feeId: 'Fee',
    },
  },
  memo: { },
};

export const EMPTY_LINE = {
  accountId: '',
  creditLessDebit: '',
  memo: '',
};

export const saleAssetColumns = props => {
  const { saleAssetAmounts, setSaleAssetAmounts, sellerInvoice } = props;

  return [
    {
      key: 'soldOn',
      label: 'Date',
      value: ({ entry }) => entry.sale.saleOn,
    },
    {
      key: 'stockNumber',
      label: 'Stock #',
      value: ({ entry }) => entry.asset.stockNumber,
    },
    {
      key: 'lotNumber',
      label: 'Lot #',
    },
    {
      key: 'soldOrIfPrice',
      label: 'Sale Price',
      value: ({ value, entry }) => (
        sellerInvoice && entry.sellerPrice ? entry.sellerPrice : value
      ),
      content: ({ value }) => currency(value),
    },
    {
      key: 'fees',
      label: 'Fees',
      value: ({ entry }) => sellerInvoice ? entry.sellerFees : entry.buyerFees,
      content: ({ value }) => currency(value),
    },
    {
      key: 'total',
      label: 'Asset Total',
      value: ({ entry }) => sellerInvoice ? -entry.sellerNet : entry.buyerNet,
      content: ({ value }) => currency(value),
    },
    {
      key: 'invoice',
      label: 'Invoice?',
      sort: 'prevent',
      content: ({ entry }) => {
        const isChecked = !!(saleAssetAmounts.find(
          saleAsset => saleAsset.id === entry.id
        ));

        return (
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => {
              if (isChecked) {
                setSaleAssetAmounts(
                  [...saleAssetAmounts.filter(item => item.id !== entry.id)]
                );
              } else {
                setSaleAssetAmounts([ ...saleAssetAmounts, entry ]);
              }
            }}
          />
        );
      }
    },
  ];
};

export const feeColumns = ({ feeAmounts, setFeeAmounts }) => (
  [
    {
      key: 'stockNumber',
      label: 'Stock #',
      value: ({ entry }) => entry.asset.stockNumber || entry.asset.vin,
    },
    {
      key: 'amount',
      label: 'Amount',
      content: ({ value }) => currency(value),
    },
    {
      key: 'type',
      label: 'Type',
    },
    {
      key: 'description',
      label: 'Description',
    },
    {
      key: 'invoice',
      label: 'Invoice?',
      sort: 'prevent',
      width: 100,
      content: ({ entry }) => {
        const isChecked = !!(feeAmounts.find(fee => fee.id === entry.id));

        return (
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => {
              if (isChecked) {
                setFeeAmounts(
                  [...feeAmounts.filter(item => item.id !== entry.id)]
                );
              } else {
                setFeeAmounts([...feeAmounts, entry]);
              }
            }}
          />
        );
      }
    },
  ]
);

export const creditMemoColumns = ({
  creditMemoAmounts, setCreditMemoAmounts, maxCreditMemo
}) => (
  [
    {
      key: 'bankDate',
      label: 'Date',
      sort: 'asc',
      value: ({ entry }) => entry.txn.bankDate,
    },
    {
      key: 'reference',
      label: 'Reference',
      value: ({ entry }) => entry.txn.reference,
    },
    {
      key: 'amount',
      label: 'Amount',
      content: ({ value }) => currency(parseFloat(value) * -1),
    },
    {
      key: 'openBalance',
      label: 'Available Balance',
      content: ({ value }) => currency(parseFloat(value * -1)),
    },
    {
      key: 'apply',
      label: 'Apply?',
      content: ({ entry }) => {
        const isChecked =
          !!(creditMemoAmounts.find(item => item.id === entry.id));

        const amount = maxCreditMemo ?
          Math.max(parseFloat(entry.openBalance), -maxCreditMemo) :
          entry.openBalance;

        return (
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => {
              if (isChecked) {
                setCreditMemoAmounts(
                  [...creditMemoAmounts.filter(item => item.id !== entry.id)]
                );
              } else {
                setCreditMemoAmounts([
                  ...creditMemoAmounts,
                  { id: entry.id, amount }
                ]);
              }
            }}
          />
        );
      }
    },
    {
      key: 'payAmount',
      label: 'Apply Amount',
      content: ({ entry }) => {
        const creditMemoAmount =
          creditMemoAmounts.find(item => item.id === entry.id);

        if (!creditMemoAmount) { return null; }

        return (
          <input
            type="text"
            value={parseFloat(creditMemoAmount.amount) * -1}
            onChange={e => {
              creditMemoAmount.amount = parseFloat(e.target.value) * -1;
              setCreditMemoAmounts([...creditMemoAmounts]);
            }}
          />
        );
      }
    },
  ]
);

const NewInvoice = props => {
  const {
    setShowModal, refetch, onCompleted, saleAsset, fee, asset, sellerInvoice,
  } = props;
  const { accounts, paymentTerms } = useContext(AuthContext);

  const processingFeeAccountId =
    specialAccount({ accounts, special: 'processing_fee' }).value;

  const emptyLines = [
    { ...EMPTY_LINE, accountId: processingFeeAccountId },
    { ...EMPTY_LINE },
  ];
  const [saleAssetAmounts, setSaleAssetAmounts] = useState([]);
  const [feeAmounts, setFeeAmounts] = useState([]);
  const [showSellFees, setShowSellFees] = useState('Sell' === fee?.type);
  const [showOtherAssetFees, setShowOtherAssetFees] = useState(false);
  const [creditMemoAmounts, setCreditMemoAmounts] = useState([]);
  const [nestedResources, setNestedResources] = useState(emptyLines);
  const [processingFeeAdded, setProcessingFeeAdded] = useState(false);
  const [formStatus, setFormStatus] = useState();
  const [mutationErrors, setMutationErrors] = useState();

  const [getInvoiceSelects, { data: selects, loading }] = useLazyQuery(
    INVOICE_SELECTS, {
      variables: { ids: [] },
      fetchPolicy: 'no-cache'
    }
  );

  const resource = { bankDate: DateTime.now().toISODate() };

  if (fee && asset) {
    if (fee.buyerFee) {
      resource.name = asset.buyer.displayName;
      resource.entityType = asset.buyer.__typename;
      resource.entityId = asset.buyer.id;
    } else {
      resource.name = asset.seller.name;
      resource.entityType = asset.seller.__typename;
      resource.entityId = asset.seller.id;
    }
  } else if (saleAsset) {
    if (sellerInvoice) {
      resource.name = saleAsset.asset.seller.name;
      resource.entityType = saleAsset.asset.seller.__typename;
      resource.entityId = saleAsset.asset.seller.id;
    } else {
      const { buyer } = saleAsset.activeOffer;
      resource.name = buyer.displayName;
      resource.entityType = buyer.__typename;
      resource.entityId = buyer.id;
    }
  };

  const {
    register, control, handleSubmit, setValue, watch, formState: { errors },
  } = useForm({
    defaultValues: defaultValues({
      fields: { ...FIELDS, entityId: {}, entityType: {}},
      resource,
      nestedFields: [{
        nestedResources,
        prefix: 'txnLinesAttributes',
        fields: LINE_FIELDS,
      }],
    })
  });

  const [createTxn] = useMutation(CREATE_TXN, {
    onCompleted: data => {
      if (data.createTxn.errors?.length) {
        setFormStatus(null);
        setMutationErrors(data.createTxn.errors);
      } else if (onCompleted) {
        onCompleted(data.createTxn.txn);
      } else {
        if (refetch) { refetch(); }
        setShowModal(false);
      }
    }
  });

  const onSubmit = useCallback(input => {
    setFormStatus('submitting');
    input.type = fee ? 'Fee Invoice' : 'Invoice';
    input.balanceAccountId =
      specialAccount({ accounts, special: 'ar' }).value;

    createTxn({
      variables: prepareTxnInput(
        input,
        FIELDS,
        {
          saleAssetAmounts,
          feeAmounts,
          creditMemoAmounts,
          accounts,
          sellerInvoice
        },
      )
    });
  });

  const watchAll = watch();

  const addLine = () => {
    const prefix = `txnLinesAttributes.${nestedResources.length}`;

    register(`${prefix}.accountId`, { value: '' });
    register(`${prefix}.creditLessDebit`, { value: '' });
    register(`${prefix}.memo`, { value: '' });
    setNestedResources([...nestedResources, EMPTY_LINE]);
  };

  useEffect(() => {
    const txnLinesWatch = watchAll?.txnLinesAttributes;

    if (txnLinesWatch) {
      const lastLine = txnLinesWatch[txnLinesWatch.length - 1];

      if (lastLine.creditLessDebit) { addLine(); }
    }
  }, [watchAll]);

  useEffect(() => {
    if (
      watchAll.entityId && ['Buyer', 'Seller'].includes(watchAll.entityType)
    ) {
      getInvoiceSelects({
        variables: {
          entityType: watchAll.entityType,
          entityId: watchAll.entityId,
          seller: sellerInvoice,
          saleAssetScopes: (
            'Seller' === watchAll.entityType ?
              ['ready_to_invoice_seller'] : ['ready_to_invoice']
          ),
        }
      });
    }
  }, [watchAll.entityId]);

  useEffect(() => {
    if (selects && !watchAll.reference) {
      setValue('reference', String(selects.settings.nextInvoiceNumber));
    }

    if (selects && saleAsset && 0 === saleAssetAmounts.length) {
      setSaleAssetAmounts(
        [...selects.saleAssets.filter(entry => entry.id === saleAsset.id)]
      );
    }

    if (selects && fee && 0 === feeAmounts.length) {
      setFeeAmounts([...selects.fees.filter(entry => entry.id === fee.id)]);
    }
  }, [selects]);

  useEffect(() => {
    if (accounts && selects && !processingFeeAdded) {
      if (parseFloat(selects.settings.processingFee)) {
        setValue(
          'txnLinesAttributes.0.creditLessDebit',
          selects.settings.processingFee
        );
      }
    }
  }, [accounts, selects, processingFeeAdded]);

  useEffect(() => {
    if (paymentTerms) {
      const defaultPaymentTerm = paymentTerms.find(entry => 2 == entry.days);
      setValue('paymentTermId', defaultPaymentTerm.id);
    }
  }, [paymentTerms]);

  const fields = { ...FIELDS };

  if (paymentTerms) {
    fields.paymentTermId.select = paymentTerms;
  }

  const lineFields = { ...LINE_FIELDS };

  const feesData = (selects?.fees || []).filter(fee => {
    if (!asset) { return false; }
    if ('0.0' === fee.amount) { return false; }
    if ('Sell' === fee.type && !showSellFees) { return false; }
    if (fee.asset.id !== asset.id && !showOtherAssetFees) { return false; }
    return true;
  });

  return (
    <View>
      <FormProvider watch={watch}>
        <Fields
          fields={fields}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        {selects?.saleAssets && !fee && (
          <Section>
            <Text>Assets Ready to Invoice for Sale</Text>

            <DataTable.Simple
              data={selects.saleAssets}
              columns={
                saleAssetColumns({
                  saleAssetAmounts,
                  setSaleAssetAmounts,
                  sellerInvoice,
                })
              }
              component={minimalTable}
              pageLength="All"
            />
          </Section>
        )}

        {loading && !selects && (
          <DataState.Loading />
        )}

        {selects?.fees && fee && (
          <Section>
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <Text>Fees Ready to Invoice</Text>

              <Pressable onPress={() => setShowSellFees(!showSellFees)}>
                <Text>[{showSellFees ? 'Hide' : 'Show'} Sell Fees]</Text>
              </Pressable>

              <Pressable
                onPress={() => setShowOtherAssetFees(!showOtherAssetFees)}
              >
                <Text>
                  [{showOtherAssetFees ? 'Hide' : 'Show'} Fees on Other Assets]
                </Text>
              </Pressable>
            </View>

            <DataTable.Simple
              data={feesData}
              columns={feeColumns({ feeAmounts, setFeeAmounts })}
              component={minimalTable}
              pageLength="All"
            />
          </Section>
        )}

        {selects?.creditMemoLines && selects.creditMemoLines.length > 0 && (
          <Section>
            <Text>Available Credit Memos</Text>

            <DataTable.Simple
              data={selects.creditMemoLines}
              columns={
                creditMemoColumns({ creditMemoAmounts, setCreditMemoAmounts })
              }
              component={minimalTable}
              pageLength="All"
            />
          </Section>
        )}

        <Section>
          <Text>Additional lines, if needed</Text>

          <NestedFields
            prefix="txnLinesAttributes"
            resources={nestedResources}
            fields={lineFields}
            control={control}
            errors={errors}
          />

          <Pressable onPress={addLine}>
            <Text style={{ fontSize: 14 }}>+ Add Line</Text>
          </Pressable>
        </Section>

        <MutationErrors errors={mutationErrors} />

        <ButtonGroup>
          <Button label="Create Invoice" onPress={handleSubmit(onSubmit)} />
        </ButtonGroup>
      </FormProvider>
    </View>
  );
};

export default NewInvoice;
